/**
 * MotionPlanWorkerService — main-thread manager for the movement-plan worker.
 *
 * Owns the Worker, sequences build requests with a monotonic requestId, and
 * rejects in-flight requests on worker error so the caller can fall back to a
 * synchronous `buildAllPlans`. Mirrors the lifecycle of CollisionWorkerService,
 * but the payloads are plain data (structured clone), so there is no geometry
 * serialization.
 *
 * Last-result-wins is enforced by the caller (`useMovementPlans` discards a
 * result whose request is no longer the latest); the worker processes requests
 * serially so results return in order.
 */
import type { BuildPlansInput, MotionWorkerInMessage, MotionWorkerOutMessage } from './motion.worker.types'
import type { MovementPlan } from '../movementPlan'

export interface BuildResult {
  plans: Record<string, MovementPlan>
  /** Pure build time inside the worker (ms). */
  buildMs: number
}

type Pending = {
  resolve: (result: BuildResult) => void
  reject: (error: Error) => void
}

export class MotionPlanWorkerService {
  private worker: Worker | null = null
  private fallback = false
  private nextId = 1
  private pending = new Map<number, Pending>()

  /** Whether the worker is currently usable (false once we have fallen back). */
  get isWorkerMode(): boolean {
    return this.worker !== null && !this.fallback
  }

  private ensure(): void {
    if (this.worker || this.fallback) return
    try {
      this.worker = new Worker(new URL('./motion.worker.ts', import.meta.url), { type: 'module' })
      this.worker.onmessage = (event: MessageEvent<MotionWorkerOutMessage>) => this.handle(event.data)
      this.worker.onerror = (event) => {
        console.error('[MotionPlanWorker] Worker error:', event.message)
        this.enableFallback()
      }
    } catch (error) {
      console.warn('[MotionPlanWorker] Failed to create worker, using synchronous fallback:', error)
      this.enableFallback()
    }
  }

  /**
   * Build all movement plans off the main thread. Rejects if the worker is
   * unavailable or errors, so the caller runs the synchronous path instead.
   */
  build(input: BuildPlansInput): Promise<BuildResult> {
    this.ensure()
    if (this.fallback || !this.worker) {
      return Promise.reject(new Error('motion worker unavailable'))
    }
    const requestId = this.nextId++
    const msg: MotionWorkerInMessage = { type: 'BUILD_PLANS', requestId, ...input }
    return new Promise<BuildResult>((resolve, reject) => {
      this.pending.set(requestId, { resolve, reject })
      this.worker!.postMessage(msg)
    })
  }

  dispose(): void {
    if (this.worker) {
      this.worker.terminate()
      this.worker = null
    }
    for (const p of this.pending.values()) p.reject(new Error('motion worker disposed'))
    this.pending.clear()
    this.fallback = false
  }

  private handle(msg: MotionWorkerOutMessage): void {
    switch (msg.type) {
      case 'WORKER_READY':
        break
      case 'PLANS_BUILT': {
        const p = this.pending.get(msg.requestId)
        if (p) {
          this.pending.delete(msg.requestId)
          p.resolve({ plans: msg.plans, buildMs: msg.buildMs })
        }
        break
      }
      case 'BUILD_ERROR': {
        const p = this.pending.get(msg.requestId)
        if (p) {
          this.pending.delete(msg.requestId)
          p.reject(new Error(msg.error))
        }
        break
      }
    }
  }

  private enableFallback(): void {
    if (this.fallback) return
    this.fallback = true
    if (this.worker) {
      this.worker.terminate()
      this.worker = null
    }
    for (const p of this.pending.values()) p.reject(new Error('motion worker fallback'))
    this.pending.clear()
  }
}

let instance: MotionPlanWorkerService | null = null

export function getMotionPlanWorkerService(): MotionPlanWorkerService {
  if (!instance) instance = new MotionPlanWorkerService()
  return instance
}

export function disposeMotionPlanWorkerService(): void {
  if (instance) {
    instance.dispose()
    instance = null
  }
}
